/**
 * The n8n workflow editor page ('workflows.*') and its admin access toggle.
 * The Arabic table must cover every key.
 */
const en = {
  'nav.workflows': 'Workflows',

  'workflows.title': 'Workflows',
  'workflows.signInHint': 'Sign in with your n8n account. No account? Ask an administrator to invite you.',
  'workflows.reload': 'Reload',
  'workflows.openNewTab': 'Open in new tab',
  'workflows.loading': 'Loading the workflow editor…',
  'workflows.frameTitle': 'n8n workflow editor',
  'workflows.notConfiguredTitle': 'The workflow editor is not set up',
  'workflows.notConfiguredBody': 'An administrator needs to set Workflows:EditorUrl to the n8n address on the server.',
  'workflows.disabledTitle': 'Workflows are not enabled for your account',
  'workflows.disabledBody': 'Ask an administrator to give you access to building workflows.',
  'workflows.launchTitle': 'Build automations in n8n',
  'workflows.launchBody': 'Design workflows that connect our systems, run on a schedule or react to events. The editor opens in its own tab; come back here any time to reopen it.',
  'workflows.launchButton': 'Open workflow editor',
  'workflows.launchAdminNote': 'Admins: to show the editor inside this page instead, the n8n server must allow being framed by this site (Content-Security-Policy: frame-ancestors). This page switches over by itself once it does.',
  'workflows.inviteBody': 'Your n8n account is ready. Set your password to start building.',
  'workflows.inviteButton': 'Finish setting up',
  'workflows.loadFailedTitle': 'Could not load the workflow editor',
  'workflows.loadFailedBody': 'The server did not answer. Check your connection and try again.',
  'workflows.retry': 'Try again',
  // Admins only: why the editor opens in its own tab instead of inside this page.
  'workflows.embedIssue.frameBlocked': "Admins: n8n refuses to be shown inside {site}. To embed it here, set N8N_CONTENT_SECURITY_POLICY=\"frame-ancestors 'self' {site}\" on the n8n server and restart n8n. This page switches over by itself once it does.",
  'workflows.embedIssue.noSiteOrigin': "Admins: this server doesn't know its own address, so it can't check whether n8n may be embedded. Set Workflows:SiteOrigin (or Email:PublicBaseUrl) to this site's address.",
  'workflows.embedIssue.unreachable': 'Admins: the n8n editor at {editor} could not be reached from this server. Check that it is up; the page embeds it when it allows that.',
  'workflows.embedIssue.mixedContent': 'Admins: this site uses https, so the n8n editor ({editor}) must use https too before it can be shown inside this page.',

  // Admin (user dialog)
  'workflows.admin.section': 'Workflows (n8n)',
  'workflows.admin.enable': 'Allow this user to open the n8n workflow editor',
  'workflows.admin.hint': 'They also need an n8n account: invite them in n8n under Settings → Users.',
  'workflows.admin.hintAuto': 'Granting access creates their n8n account; they finish setting it up from the Workflows page.',
  'workflows.admin.invitePending': 'Their n8n account is waiting for them to finish setting it up.',
  'workflows.admin.accountCreated': 'An n8n account was created; they can finish setting it up from the Workflows page.',
  'workflows.admin.updateFailed': 'The user was saved, but workflow access could not be updated.',

  // Publishing a workflow as an agent (admins)
  'workflows.publish.open': 'Publish to chat',
  'workflows.publish.title': 'Publish a workflow to chat',
  'workflows.publish.intro': 'A published workflow becomes an agent: people pick it in Chat and Scheduled tasks, and it answers through its n8n trigger. Only workflows with a "When chat message received" or webhook trigger are listed.',
  'workflows.publish.search': 'Search workflows',
  'workflows.publish.empty': 'No workflows with a chat or webhook trigger yet.',
  'workflows.publish.noMatch': 'No workflows match your search.',
  'workflows.publish.loadFailed': 'Could not load the workflows from n8n.',
  'workflows.publish.triggerChat': 'Chat trigger',
  'workflows.publish.triggerWebhook': 'Webhook trigger',
  'workflows.publish.live': 'Ready · live in n8n',
  'workflows.publish.notLive': 'Ready · will be published in n8n',
  'workflows.publish.basicAuth': 'needs its Basic Auth login',
  'workflows.publish.publishedAs': 'Published as "{name}"',
  'workflows.publish.publish': 'Publish',
  'workflows.publish.publishOne': 'Publish {name}',
  'workflows.publish.formIntro': 'Publishing "{workflow}" as an agent.',
  'workflows.publish.willActivate': 'It is not live yet, so it will also be published in n8n.',
  'workflows.publish.name': 'Agent name',
  'workflows.publish.description': 'Description',
  'workflows.publish.descriptionPlaceholder': 'What people can ask it, shown in the agent list.',
  'workflows.publish.visibility': 'Who can use it',
  'workflows.publish.visibilityPerUser': 'Only users I give access to (in Manage Agents)',
  'workflows.publish.visibilityRestricted': 'Everyone can see it, only users I give access to can use it',
  'workflows.publish.visibilityPublic': 'Everyone',
  'workflows.publish.authTitle': 'Trigger login',
  'workflows.publish.authHint': 'This trigger uses Basic Auth. Enter the username and password from its n8n credential; they are stored encrypted.',
  'workflows.publish.username': 'Username',
  'workflows.publish.password': 'Password',
  'workflows.publish.back': 'Back',
  'workflows.publish.submit': 'Publish agent',
  'workflows.publish.done': '"{name}" is now an agent.',
  'workflows.publish.doneActivated': '"{name}" is now an agent, and the workflow is live in n8n.',
  'workflows.publish.failed': 'The workflow could not be published.',
  'workflows.publish.problem.chatNotPublic': 'In n8n, turn on "Make Chat Publicly Available" in the chat trigger.',
  'workflows.publish.problem.webhookNotPost': 'In n8n, set the webhook\'s HTTP Method to POST.',
  'workflows.publish.problem.respondsImmediately': 'In n8n, set the webhook\'s Respond option to "When Last Node Finishes" or "Using \'Respond to Webhook\' Node", so it answers with the result.',
  'workflows.publish.problem.pathHasParameters': 'The webhook path has parameters (like :id); use a fixed path.',
  'workflows.publish.problem.triggerDisabled': 'The trigger node is deactivated in n8n.',
  'workflows.publish.problem.noTrigger': 'The trigger has no address yet; open and save the workflow in n8n.',
};

const ar: Record<keyof typeof en, string> = {
  'nav.workflows': 'سير العمل',

  'workflows.title': 'سير العمل',
  'workflows.signInHint': 'سجّل الدخول بحسابك في n8n. ليس لديك حساب؟ اطلب من المسؤول دعوتك.',
  'workflows.reload': 'إعادة التحميل',
  'workflows.openNewTab': 'فتح في علامة تبويب جديدة',
  'workflows.loading': 'جارٍ تحميل محرر سير العمل…',
  'workflows.frameTitle': 'محرر سير العمل n8n',
  'workflows.notConfiguredTitle': 'محرر سير العمل غير مُعدّ',
  'workflows.notConfiguredBody': 'يحتاج المسؤول إلى ضبط Workflows:EditorUrl على عنوان n8n في الخادم.',
  'workflows.disabledTitle': 'سير العمل غير مفعّل لحسابك',
  'workflows.disabledBody': 'اطلب من المسؤول منحك صلاحية بناء سير العمل.',
  'workflows.launchTitle': 'ابنِ الأتمتة في n8n',
  'workflows.launchBody': 'صمّم سير عمل يربط أنظمتنا، أو يعمل وفق جدول، أو يستجيب للأحداث. يفتح المحرر في علامة تبويب خاصة به، ويمكنك العودة إلى هنا لإعادة فتحه في أي وقت.',
  'workflows.launchButton': 'فتح محرر سير العمل',
  'workflows.launchAdminNote': 'للمسؤولين: لعرض المحرر داخل هذه الصفحة، يجب أن يسمح خادم n8n بتضمينه في هذا الموقع (Content-Security-Policy: frame-ancestors). تنتقل الصفحة إلى ذلك تلقائيًا بمجرد السماح.',
  'workflows.inviteBody': 'حسابك في n8n جاهز. عيّن كلمة المرور لتبدأ البناء.',
  'workflows.inviteButton': 'إكمال الإعداد',
  'workflows.loadFailedTitle': 'تعذّر تحميل محرر سير العمل',
  'workflows.loadFailedBody': 'لم يستجب الخادم. تحقّق من اتصالك وحاول مرة أخرى.',
  'workflows.retry': 'حاول مرة أخرى',
  'workflows.embedIssue.frameBlocked': "للمسؤولين: يرفض n8n عرضه داخل {site}. لتضمينه هنا، اضبط N8N_CONTENT_SECURITY_POLICY=\"frame-ancestors 'self' {site}\" على خادم n8n ثم أعد تشغيله. تنتقل الصفحة إلى ذلك تلقائيًا بمجرد السماح.",
  'workflows.embedIssue.noSiteOrigin': 'للمسؤولين: لا يعرف هذا الخادم عنوانه، لذا لا يمكنه التحقق من إمكانية تضمين n8n. اضبط Workflows:SiteOrigin (أو Email:PublicBaseUrl) على عنوان هذا الموقع.',
  'workflows.embedIssue.unreachable': 'للمسؤولين: تعذّر الوصول إلى محرر n8n على {editor} من هذا الخادم. تحقّق من أنه يعمل؛ تضمّنه الصفحة عندما يسمح بذلك.',
  'workflows.embedIssue.mixedContent': 'للمسؤولين: يستخدم هذا الموقع https، لذا يجب أن يستخدم محرر n8n ({editor}) https أيضًا قبل عرضه داخل هذه الصفحة.',

  'workflows.admin.section': 'سير العمل (n8n)',
  'workflows.admin.enable': 'السماح لهذا المستخدم بفتح محرر سير العمل n8n',
  'workflows.admin.hint': 'يحتاج أيضًا إلى حساب في n8n: ادعُه من n8n عبر الإعدادات ← المستخدمون.',
  'workflows.admin.hintAuto': 'منح الصلاحية ينشئ حسابه في n8n، ويكمل إعداده من صفحة سير العمل.',
  'workflows.admin.invitePending': 'حسابه في n8n بانتظار أن يكمل إعداده.',
  'workflows.admin.accountCreated': 'تم إنشاء حساب في n8n، ويمكنه إكمال إعداده من صفحة سير العمل.',
  'workflows.admin.updateFailed': 'تم حفظ المستخدم، لكن تعذّر تحديث صلاحية سير العمل.',

  'workflows.publish.open': 'النشر في المحادثة',
  'workflows.publish.title': 'نشر سير عمل في المحادثة',
  'workflows.publish.intro': 'يصبح سير العمل المنشور وكيلًا: يختاره المستخدمون في المحادثة والمهام المجدولة، ويجيب عبر مشغّله في n8n. تظهر هنا فقط سير العمل التي تحتوي على مشغّل "When chat message received" أو Webhook.',
  'workflows.publish.search': 'البحث في سير العمل',
  'workflows.publish.empty': 'لا يوجد بعد سير عمل بمشغّل محادثة أو Webhook.',
  'workflows.publish.noMatch': 'لا توجد نتائج مطابقة لبحثك.',
  'workflows.publish.loadFailed': 'تعذّر تحميل سير العمل من n8n.',
  'workflows.publish.triggerChat': 'مشغّل محادثة',
  'workflows.publish.triggerWebhook': 'مشغّل Webhook',
  'workflows.publish.live': 'جاهز · منشور في n8n',
  'workflows.publish.notLive': 'جاهز · سيُنشر في n8n',
  'workflows.publish.basicAuth': 'يحتاج بيانات دخول Basic Auth',
  'workflows.publish.publishedAs': 'منشور باسم "{name}"',
  'workflows.publish.publish': 'نشر',
  'workflows.publish.publishOne': 'نشر {name}',
  'workflows.publish.formIntro': 'نشر "{workflow}" كوكيل.',
  'workflows.publish.willActivate': 'لم يُنشر بعد في n8n، لذا سيُنشر هناك أيضًا.',
  'workflows.publish.name': 'اسم الوكيل',
  'workflows.publish.description': 'الوصف',
  'workflows.publish.descriptionPlaceholder': 'ما الذي يمكن سؤاله عنه، ويظهر في قائمة الوكلاء.',
  'workflows.publish.visibility': 'من يمكنه استخدامه',
  'workflows.publish.visibilityPerUser': 'فقط المستخدمون الذين أمنحهم الصلاحية (من إدارة الوكلاء)',
  'workflows.publish.visibilityRestricted': 'يراه الجميع، ولا يستخدمه إلا من أمنحهم الصلاحية',
  'workflows.publish.visibilityPublic': 'الجميع',
  'workflows.publish.authTitle': 'بيانات دخول المشغّل',
  'workflows.publish.authHint': 'يستخدم هذا المشغّل Basic Auth. أدخل اسم المستخدم وكلمة المرور من بيانات الاعتماد في n8n؛ تُحفظ مشفّرة.',
  'workflows.publish.username': 'اسم المستخدم',
  'workflows.publish.password': 'كلمة المرور',
  'workflows.publish.back': 'رجوع',
  'workflows.publish.submit': 'نشر الوكيل',
  'workflows.publish.done': 'أصبح "{name}" وكيلًا.',
  'workflows.publish.doneActivated': 'أصبح "{name}" وكيلًا، وسير العمل منشور الآن في n8n.',
  'workflows.publish.failed': 'تعذّر نشر سير العمل.',
  'workflows.publish.problem.chatNotPublic': 'في n8n، فعّل خيار "Make Chat Publicly Available" في مشغّل المحادثة.',
  'workflows.publish.problem.webhookNotPost': 'في n8n، اجعل HTTP Method للـ Webhook هو POST.',
  'workflows.publish.problem.respondsImmediately': 'في n8n، اضبط خيار Respond للـ Webhook على "When Last Node Finishes" أو "Using \'Respond to Webhook\' Node" ليجيب بالنتيجة.',
  'workflows.publish.problem.pathHasParameters': 'مسار الـ Webhook يحتوي على متغيرات (مثل :id)؛ استخدم مسارًا ثابتًا.',
  'workflows.publish.problem.triggerDisabled': 'عقدة المشغّل معطّلة في n8n.',
  'workflows.publish.problem.noTrigger': 'ليس للمشغّل عنوان بعد؛ افتح سير العمل واحفظه في n8n.',
};

const workflows = { en, ar };
export default workflows;
